/**
 * Single source of truth for "does this country require a province/state in
 * the shipping address?". US (states) and CA (provinces) are the only
 * ISO-3166 alpha-2 codes where address validation, PayPal `admin_area_1`,
 * and Shippo rate quoting expect a non-empty value. Every other country
 * (FR, UK, JP, DE, …) leaves state blank.
 *
 * Why centralised: the rule used to live in 6 places (settings AddressBook,
 * shipping-form internal validation, shipping-form render gate, checkout-form
 * pre-render gate, export-orders, and the Shippo Zod schema). They drifted —
 * buyer-side `isShippingAddressComplete` required state for ALL countries
 * while `ShippingForm` only required it for US/CA, so the address-book
 * preselect for any non-US/CA buyer silently produced `formData.state = ""`
 * that the gate then rejected. PayPal/Stripe buttons and the live-rate
 * subflow both depend on that gate → empty checkout, no error.
 * Incident: 2026-06-25 (Anton, FR).
 *
 * Accepts ISO-3166 alpha-2 ("US"), or empty/null — any non-US/CA value
 * (including unknown / not-yet-selected) returns false. Long-form names
 * ("United States", "Canada") must be normalised to ISO at the caller
 * (e.g. via `getCountryCodeByName` from `lib/paypal-countries.ts`); this
 * module deliberately stays dependency-free so it can ship in
 * `@kraahl/contract`. Trim + uppercase before comparing so callers don't
 * need to pre-normalise the ISO form.
 */
export function isStateRequiredForCountry(country: string | null | undefined): boolean {
  if (!country) return false;
  const iso = country.trim().toUpperCase();
  return iso === "US" || iso === "CA";
}
